<template>
  <section
    class="flex h-full w-full flex-col justify-between gap-4 rounded-md border border-light-gray p-4"
  >
    <h3 class="text-left text-base text-dark-gray lg:text-xl">
      Secure Communications
    </h3>

    <!-- Rate Confirmation Contacts -->
    <section class="flex h-full flex-col gap-4 lg:flex-row">
      <div class="h-full flex-1 rounded-md border border-light-gray p-4">
        <section class="flex flex-col items-start gap-4">
          <h1 class="text-dark-blue">Rate Confirmation Contacts</h1>
          <p class="mt-2 text-left text-xs text-body-color">
            {{ rateConfirmationMessage }}
          </p>
          <template v-if="displayEmptyState">
            <RequireVerification
              type="rateConfirmation"
              class="h-full min-h-50 w-full flex-1"
            />
          </template>
          <template v-else>
            <RateCards
              class="w-full"
              :title="'Email Addresses'"
              :data="[...emails, ...domains]"
            />
            <p class="mt-2 text-left text-xs text-body-color">
              All rate confirmation concerns can be directed to these phone
              numbers.
            </p>
            <RateCards
              class="w-full"
              :title="'Phone Numbers'"
              :data="phones"
            />
          </template>
        </section>
      </div>

      <!-- Invoicing Contacts -->
      <div class="flex h-full flex-1 rounded-md border border-light-gray p-4">
        <section class="flex flex-col items-start gap-4">
          <h1 class="text-dark-blue">Invoicing Contacts</h1>
          <p class="mt-2 text-left text-xs text-body-color">
            {{ invoicingMessage }}
          </p>
          <template v-if="displayEmptyState">
            <RequireVerification
              type="billingContacts"
              class="h-full min-h-50 w-full flex-1"
            />
          </template>
          <template v-else>
            <section
              v-if="invoicingContacts.length === 0"
              class="flex h-full w-full items-center justify-center rounded-md border border-light-gray bg-off-white py-4"
            >
              <p class="text-xs text-body-color">No known invoicing contacts</p>
            </section>
            <section
              v-for="(contact, idc) in invoicingContacts"
              v-else
              :key="idc"
              class="w-full"
            >
              <InvoicingCard
                :contact="contact"
                class="w-full"
              />
            </section>
          </template>
        </section>
      </div>

      <!-- Digital Activities -->
      <div class="flex h-full flex-1 rounded-md border border-light-gray p-4">
        <section class="flex flex-col items-start gap-4">
          <h1 class="text-dark-blue">Digital Activity</h1>
          <p class="mt-2 text-left text-xs text-body-color">
            {{ digitalActivityMessage }}
          </p>
          <template v-if="displayEmptyStateDigital">
            <RequireVerification
              type="digitalActivity"
              class="h-full min-h-50 w-full flex-1"
            />
          </template>
          <template v-else>
            <DigitalActivity
              class="w-full"
              :activities="digitalActivities"
            />
          </template>
        </section>
      </div>
    </section>
  </section>
</template>
<script setup>
import { computed, defineProps } from 'vue';
import RateCards from '@/components/show/reusable/RateCards.vue';
import InvoicingCard from '@/components/show/reusable/InvoicingCard.vue';
import RequireVerification from '@/components/show/reusable/RequireVerification.vue';
import DigitalActivity from '@/components/show/DigitalActivity.vue';

const props = defineProps({
  rateConInformation: {
    type: Object,
    default: () => ({}),
  },
  invoicingContacts: {
    type: Array,
    default: () => [],
  },
  digitalActivities: {
    type: Array,
    default: () => [],
  },
});

const emails = computed(() => {
  return (
    props.rateConInformation.email_addresses?.map((info) => {
      return {
        value: info,
        type: 'email',
      };
    }) || []
  );
});

const domains = computed(() => {
  return (
    props.rateConInformation.company_domains?.map((info) => {
      return {
        value: info,
        type: 'domain',
      };
    }) || []
  );
});

const phones = computed(() => {
  return (
    props.rateConInformation.phones?.map((info) => {
      return {
        value: info,
        type: 'phone',
      };
    }) || []
  );
});

const displayEmptyState = computed(() => {
  return (
    emails.value.length === 0 &&
    domains.value.length === 0 &&
    phones.value.length === 0
  );
});

const displayEmptyStateDigital = computed(() => {
  return props.digitalActivities.length === 0;
});

const rateConfirmationMessage = computed(() =>
  displayEmptyState.value
    ? 'No rate confirmation contacts have been added by this broker.'
    : 'All rate confirmations will only come from these email addresses/domains.'
);
const invoicingMessage = computed(() =>
  displayEmptyState.value
    ? 'No invoicing contacts have been added by this broker.'
    : 'All broker invoices must be sent to this invoicing contact(s).'
);

const digitalActivityMessage = computed(() =>
  displayEmptyStateDigital.value
    ? 'No recent digital activity has been recorded for this broker.'
    : 'There has been recent digital activity from these locations.'
);
</script>
